import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod/v4';

import {
  schema,
  USER_ROLES_VALUES,
  USER_THEMES_VALUES,
  STATUS_VALUES,
} from '@edgevanta/db/drizzle/client';

export { emailSchema } from '../common/email.schema';
export const otpSchema = z.string().min(6);

export const selectUserSchema = createSelectSchema(schema.users, {
  image: z.string().nullable(),
  role: z.enum(USER_ROLES_VALUES),
  theme: z.enum(USER_THEMES_VALUES).nullable(),
  status: z.enum(STATUS_VALUES),
});

export const insertUserSchema = createInsertSchema(schema.users, {
  email: z.string().email('Invalid email address'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  theme: z.enum(USER_THEMES_VALUES),
  role: z.enum(USER_ROLES_VALUES),
});

export const updateUserSchema = insertUserSchema.partial().extend({
  id: z.string(),
});

const UsersQuerySchema = z.object({
  search: z.string().optional(),
});

export const getUsersQuerySchema = UsersQuerySchema.merge(
  z.object({
    limit: z.coerce.number().optional().default(25),
    offset: z.coerce.number().optional().default(0),
    page: z.coerce.number().optional(),
    email: z.string().optional(),
    status: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((status) => status);
      })
      .default([]),
    role: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((role) => role);
      })
      .default([]),
    createdAt: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((num) => Number(num));
      })
      .default([]),
    sort: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [
            {
              id: 'email',
              desc: false,
            },
          ];
        }

        try {
          const parsed = JSON.parse(val);
          const sortSchema = z.array(
            z.object({
              id: z.string(),
              desc: z.boolean(),
            }),
          );

          const result = sortSchema.safeParse(parsed);

          if (!result.success) {
            return [
              {
                id: 'email',
                desc: false,
              },
            ];
          }

          return result.data;
        } catch {
          return [
            {
              id: 'email',
              desc: false,
            },
          ];
        }
      })
      .default([
        {
          id: 'email',
          desc: false,
        },
      ]),
  }),
);

export const getUserOrganizationsQuerySchema = z.object({
  limit: z.coerce.number().optional().default(10),
  offset: z.coerce.number().optional().default(0),
  organizationName: z.string().optional(),
  role: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }
      return val.split(',').map((role) => role);
    })
    .default([]),
  createdAt: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((num) => Number(num));
    })
    .default([]),
  sort: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [
          {
            id: 'organizationName',
            desc: false,
          },
        ];
      }

      try {
        const parsed = JSON.parse(val);
        const sortSchema = z.array(
          z.object({
            id: z.string(),
            desc: z.boolean(),
          }),
        );

        const result = sortSchema.safeParse(parsed);

        if (!result.success) {
          return [
            {
              id: 'organizationName',
              desc: false,
            },
          ];
        }

        return result.data;
      } catch {
        return [
          {
            id: 'organizationName',
            desc: false,
          },
        ];
      }
    })
    .default([
      {
        id: 'organizationName',
        desc: false,
      },
    ]),
});

export const getUsersCountQuerySchema = UsersQuerySchema;
